// NOTE(liamvdv): middlewares help us sync the redux state with external services/apis/persistent storage
import { Middleware, UnknownAction } from "@reduxjs/toolkit";
import { refreshTheme } from "../slices/configSlice";
import i18n, { i18nOptions } from "../i18n";
import { selectEffectiveLocale } from "./selectors";
import { updateUserDataAndPing } from "../slices/userSlice";
import { pingFrameWithCurrentState } from "./sharedActions";
import { setChallengeHeadersAndPing } from "../slices/challengeSlice";
import { Impl } from "../sdk";

export const createThemeRefreshMiddleware = (): Middleware => {
  return (store) => {
    // Force a re-render when system theme changes by dispatching an empty action
    // This will cause the selector to recompute with the new system theme
    const darkModeMediaQuery = window.matchMedia(
      "(prefers-color-scheme: dark)"
    );
    darkModeMediaQuery.addEventListener("change", () => {
      store.dispatch(refreshTheme());
    });

    return (next) => (action) => next(action);
  };
};

export const createLocaleMiddleware = (): Middleware => {
  return (store) => {
    // Function to sync locale with i18n
    const syncI18nLocale = () => {
      const state = store.getState();
      const newLocale = state.config.data.locale;
      const frameLocale = selectEffectiveLocale(state);

      // TODO(liamvdv): we can also force that our UI and locale must be the same.
      if (newLocale) {
        // Check if locale is supported in i18n
        const bestMatch = findBestI18nMatch(
          newLocale,
          Object.keys(i18nOptions.resources)
        );
        if (bestMatch) {
          i18n.changeLanguage(bestMatch);
        }
      }
    };

    // Initial sync
    syncI18nLocale();

    // Listen for state changes
    return (next) => (action) => {
      const result = next(action);
      syncI18nLocale();
      return result;
    };
  };
};

const findBestI18nMatch = (
  locale: string,
  available: readonly string[]
): string => {
  const normalized = locale.toLowerCase();

  // Try exact match
  const exact = available.find((l) => l.toLowerCase() === normalized);
  if (exact) return exact;

  // Try language-only match
  const language = normalized.split("-")[0];
  const partial = available.find(
    (l) => l.toLowerCase().split("-")[0] === language
  );
  if (partial) return partial;

  // Return default fallback
  return "en";
};

export const createSyncUserDataMiddleware =
  (): Middleware => (store) => (next) => (action) => {
    const result = next(action);

    if ((action as UnknownAction).type === updateUserDataAndPing.type) {
      store.dispatch(
        pingFrameWithCurrentState({
          refresh: true,
          background: true,
        }) as any
      );
    }

    if ((action as UnknownAction).type === setChallengeHeadersAndPing.type) {
      store.dispatch(
        pingFrameWithCurrentState({
          refresh: true,
          background: true,
        }) as any
      );
    }

    return result;
  };

export const createSdkEventMiddleware =
  (): Middleware => (store) => (next) => (action) => {
    const prevState = store.getState().launcher.isOpen;
    const result = next(action);
    const nextState = store.getState().launcher.isOpen;
    if (prevState !== nextState && window.$botbrains instanceof Impl)
      window.$botbrains.emit(nextState ? "chat.opened" : "chat.closed");

    return result;
  };
